import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";

import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QHubBuddyLogo, QHubFooter } from "@/components/QHubBranding";
import { KeyRound, CheckCircle2, Eye, EyeOff } from "lucide-react";

const MIN_PASSWORD_LEN = 8; // preserves current invite UX minimum

const ResetPasswordB2C = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const mismatch = password && confirm && password !== confirm;
  const tooShort = password && password.length < MIN_PASSWORD_LEN;

  const canSubmit =
    !!token && !!password && password === confirm && !tooShort && !loading;

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password-v2`, { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1400);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "Invalid token") {
        setError(
          "Este enlace no es válido. Solicita uno nuevo desde “¿Olvidaste tu contraseña?”.",
        );
      } else if (detail === "Token expired") {
        setError(
          "Este enlace ha expirado. Solicita uno nuevo desde “¿Olvidaste tu contraseña?”.",
        );
      } else if (detail === "Token already used") {
        setError(
          "Este enlace ya fue utilizado. Solicita uno nuevo si necesitas volver a restablecer tu contraseña.",
        );
      } else {
        setError(detail || "No se pudo restablecer la contraseña. Intenta de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-mesh" data-testid="reset-password-b2c-page">
      <header className="qhub-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <QHubBuddyLogo size="default" />
            <Link
              to="/login"
              className="text-sm text-gray-600 hover:text-gray-900"
              data-testid="reset-back-to-login"
            >
              Volver al login
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bg-white rounded-3xl p-8 border border-gray-100 shadow-xl shadow-gray-200/50">
          {!token ? (
            <div data-testid="reset-invalid-token">
              <h1 className="text-xl font-bold font-['Outfit'] text-gray-900 text-center mb-2">
                Enlace inválido
              </h1>
              <p className="text-sm text-gray-600 text-center mb-6">
                No pudimos leer el token de recuperación. Solicita un nuevo enlace desde la
                página de inicio de sesión.
              </p>
              <Button
                onClick={() => navigate("/buddy-avanza/forgot-password")}
                className="w-full h-11 rounded-xl bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white"
                data-testid="reset-request-new-link"
              >
                Solicitar un enlace nuevo
              </Button>
            </div>
          ) : done ? (
            <div data-testid="reset-success">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="text-xl font-bold font-['Outfit'] text-gray-900 text-center mb-2">
                Contraseña actualizada
              </h1>
              <p className="text-sm text-gray-600 text-center">
                Tu nueva contraseña quedó activa. Te llevaremos al login en un momento…
              </p>
            </div>
          ) : (
            <>
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#419cca] to-[#8b5cf6] flex items-center justify-center shadow-lg shadow-[#419cca]/20">
                <KeyRound className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-xl font-bold font-['Outfit'] text-gray-900 text-center mb-2">
                Restablecer contraseña
              </h1>
              <p className="text-sm text-gray-500 text-center mb-6">
                Elige una nueva contraseña para tu cuenta de BUDDY AVANZA.
              </p>

              <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                <div className="relative">
                  <Input
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Nueva contraseña"
                    type={showPw ? "text" : "password"}
                    autoComplete="new-password"
                    className="h-12 rounded-xl pr-10"
                    data-testid="reset-password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="reset-toggle-password-visibility"
                    aria-label={showPw ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                <Input
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirmar contraseña"
                  type={showPw ? "text" : "password"}
                  autoComplete="new-password"
                  className="h-12 rounded-xl"
                  data-testid="reset-password-confirm-input"
                  required
                />

                {tooShort && (
                  <p className="text-xs text-amber-700" data-testid="reset-error-too-short">
                    La contraseña debe tener al menos {MIN_PASSWORD_LEN} caracteres.
                  </p>
                )}
                {mismatch && (
                  <p className="text-xs text-red-600" data-testid="reset-error-mismatch">
                    Las contraseñas no coinciden.
                  </p>
                )}
                {error && (
                  <p
                    className="text-sm text-red-600 bg-red-50 rounded-lg p-2.5"
                    data-testid="reset-error"
                  >
                    {error}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-[#419cca] to-[#8b5cf6] hover:opacity-90 text-white font-medium text-base"
                  data-testid="reset-submit-btn"
                >
                  {loading ? "Actualizando…" : "Actualizar contraseña"}
                </Button>
              </form>
            </>
          )}
        </div>
      </main>

      <QHubFooter />
    </div>
  );
};

export default ResetPasswordB2C;
